import type {
  ConsoleAsset,
  ConsoleBrandDetail,
  RoleCoverage,
} from '@rsecure/brand-contracts/console';
import { useState } from 'react';
import { Badge, Ltr, Notice } from '../../components/ui';
import { fmtBytes } from '../../lib/format';

/** An <img> of a PUBLIC object that reports honestly when the CDN does not serve it. */
function PublicImg({ url, alt }: { url: string; alt: string }) {
  const [broken, setBroken] = useState(false);
  if (broken)
    return (
      <span className="missing" role="img" aria-label={`${alt}: public object not reachable`}>
        Public object not reachable
        <br />
        <Ltr mono>{url.split('/').slice(-2).join('/').slice(0, 28)}…</Ltr>
      </span>
    );
  return <img src={url} alt={alt} onError={() => setBroken(true)} />;
}

/** The published light + dark logo pair on white and on #121212 (the ink-measurement reference). */
export function LogoStage({
  name,
  light,
  dark,
  compact,
}: {
  name: string;
  light: string | null;
  dark: string | null;
  compact?: boolean;
}) {
  return (
    <div className="stage-pair" style={compact ? undefined : { minHeight: 180 }}>
      <div className="stage light">
        <span className="cap">On light</span>
        {light ? (
          <PublicImg url={light} alt={`${name} logo on light background`} />
        ) : (
          <span className="missing">
            No logo_on_light published — campaigns render a wordmark on white.
          </span>
        )}
      </div>
      <div className="stage dark">
        <span className="cap">On dark</span>
        {dark ? (
          <PublicImg url={dark} alt={`${name} logo on dark background`} />
        ) : (
          <span className="missing">No logo_on_dark published.</span>
        )}
      </div>
    </div>
  );
}

const ROLE_HELP: Record<string, string> = {
  logo_on_light: 'Legible on light backgrounds (email white header)',
  logo_on_dark: 'Legible on dark / brand-colored backgrounds',
  wordmark: 'Horizontal name mark',
  symbol: 'Standalone symbol / monogram',
  icon: 'Square app icon / avatar',
};

function RoleCard({ r, name }: { r: RoleCoverage; name: string }) {
  return (
    <div className="asset" data-testid={`role-${r.role}`}>
      <div className="pv">
        <div style={{ background: '#fff' }}>
          {r.publicUrl ? (
            <PublicImg url={r.publicUrl} alt={`${name} ${r.role} on light`} />
          ) : (
            <span className="faint" style={{ fontSize: 12 }}>
              —
            </span>
          )}
        </div>
        <div style={{ background: 'var(--ink-dark-bg)' }}>
          {r.publicUrl ? (
            <PublicImg url={r.publicUrl} alt={`${name} ${r.role} on dark`} />
          ) : (
            <span style={{ fontSize: 12, color: '#777' }}>—</span>
          )}
        </div>
      </div>
      <div className="meta">
        <div className="row">
          <strong className="mono">{r.role}</strong>
          {!r.present ? (
            <Badge tone="warn">missing</Badge>
          ) : r.underReview ? (
            <Badge tone="warn" title="An open ambiguous_ink review targets this placement">
              ambiguous — under review
            </Badge>
          ) : (
            <Badge tone="ok">assigned</Badge>
          )}
          {r.present && !r.publicUrl && <Badge tone="warn">not public</Badge>}
        </div>
        <div className="muted">{ROLE_HELP[r.role]}</div>
        {r.present ? (
          <div className="muted">
            ink <strong>{r.ink}</strong> · confidence {r.inkConfidence} · basis {r.basis}
            <br />
            {r.mime} · {r.width ?? '?'}×{r.height ?? '?'} · <Ltr mono>{r.sha256?.slice(0, 12)}</Ltr>
          </div>
        ) : (
          <div className="faint">No asset holds this role</div>
        )}
      </div>
    </div>
  );
}

function AssetCard({ a, name }: { a: ConsoleAsset; name: string }) {
  const pub = a.publicObjects[0]?.url ?? null;
  const an = a.analysis as {
    ink?: string;
    inkConfidence?: number;
    visibleOnLight?: number;
    visibleOnDark?: number;
    svgProblems?: string[];
  };
  return (
    <div className="asset" data-testid="asset-card">
      <div className="pv">
        {pub ? (
          <>
            <div style={{ background: '#fff' }}>
              <PublicImg url={pub} alt={`${name} asset on light`} />
            </div>
            <div style={{ background: 'var(--ink-dark-bg)' }}>
              <PublicImg url={pub} alt={`${name} asset on dark`} />
            </div>
          </>
        ) : (
          <div
            style={{
              gridColumn: '1 / 3',
              background: 'var(--sunken)',
              textAlign: 'center',
              fontSize: 12.5,
            }}
            className="muted"
          >
            Stored privately only — never published.
            <br />
            Private originals are not exposed by the console.
          </div>
        )}
      </div>
      <div className="meta">
        <div className="row">
          <strong>{a.format.toUpperCase()}</strong>
          <span className="muted">
            {a.width ?? '?'}×{a.height ?? '?'} · {fmtBytes(a.bytes)}
            {a.hasAlpha ? ' · alpha' : ''}
          </span>
          {a.activeRoles.map((r) => (
            <Badge key={r} tone="accent">
              {r}
            </Badge>
          ))}
          {a.status !== 'stored' && <Badge tone="warn">{a.status}</Badge>}
        </div>
        <div className="muted">
          measured ink <strong>{an.ink ?? '—'}</strong> ({an.inkConfidence ?? '—'}) · visible on
          light {an.visibleOnLight ?? '—'} · on dark {an.visibleOnDark ?? '—'}
        </div>
        <div className="muted">
          source {a.sourceKind}
          {a.brandfetchType
            ? ` · provider label ${a.brandfetchType}/${a.brandfetchTheme ?? '—'}`
            : ''}
        </div>
        <div className="muted" style={{ overflowWrap: 'anywhere' }}>
          sha256 <Ltr mono>{a.sha256}</Ltr>
        </div>
        <div>
          {a.publicObjects.length ? (
            <Badge tone="ok" title={a.publicObjects.map((o) => o.key).join('\n')}>
              public · verified
            </Badge>
          ) : (
            <Badge>private only</Badge>
          )}
          {a.roles.length > 0 && a.activeRoles.length === 0 && (
            <span className="muted" style={{ fontSize: 12 }}>
              {' '}
              held roles in r#{[...new Set(a.roles.map((r) => r.revisionNumber))].join(', r#')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export function AssetsTab({ d }: { d: ConsoleBrandDetail }) {
  const light = d.roleCoverage.find((r) => r.role === 'logo_on_light');
  const dark = d.roleCoverage.find((r) => r.role === 'logo_on_dark');
  const missing = d.roleCoverage.filter((r) => !r.present).map((r) => r.role);
  const ambiguous = d.roleCoverage.filter((r) => r.underReview).map((r) => r.role);
  return (
    <div className="grid" style={{ gap: 18 }}>
      <LogoStage
        name={d.entity.canonicalName}
        light={light?.publicUrl ?? null}
        dark={dark?.publicUrl ?? null}
      />
      {(missing.length > 0 || ambiguous.length > 0) && d.content && (
        <Notice tone="warn">
          {missing.length > 0 && <>Missing roles: {missing.join(', ')}. </>}
          {ambiguous.length > 0 && <>Ambiguous placement under review: {ambiguous.join(', ')}.</>}
        </Notice>
      )}
      <section aria-labelledby="h-roles">
        <h2 id="h-roles" style={{ fontSize: 15, margin: '0 0 10px' }}>
          Logo roles (active revision)
        </h2>
        {d.content ? (
          <div className="asset-grid">
            {d.roleCoverage.map((r) => (
              <RoleCard key={r.role} r={r} name={d.entity.canonicalName} />
            ))}
          </div>
        ) : (
          <p className="muted">No revision — no role has been assigned.</p>
        )}
      </section>
      <section aria-labelledby="h-all">
        <h2 id="h-all" style={{ fontSize: 15, margin: '0 0 10px' }}>
          All stored assets <span className="muted">({d.assets.length})</span>
        </h2>
        {d.assets.length ? (
          <div className="asset-grid">
            {d.assets.map((a) => (
              <AssetCard key={a.id} a={a} name={d.entity.canonicalName} />
            ))}
          </div>
        ) : (
          <p className="muted">No assets stored for this entity.</p>
        )}
      </section>
    </div>
  );
}
